import { Link } from 'react-router-dom';
import { Play, Youtube } from 'lucide-react';
import { Button } from '../components/ui/button';

const Videos = () => {
  // Featured video
  const featuredVideo = {
    id: 'S9tUiboNqS0',
    title: 'Introduction à la Division Immobilière',
    description: 'Découvrez les bases de la division immobilière et comment optimiser vos investissements.'
  };

  return (
    <div className="pt-20" data-testid="videos-page">
      {/* Hero */}
      <section className="bg-[#1e3a5f] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-[#c9a227] uppercase tracking-[0.2em] text-sm font-semibold mb-4">
              Vidéos
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-white mb-6">
              Espace Vidéo
            </h1>
            <p className="text-gray-300 text-lg leading-relaxed">
              Retrouvez nos conseils et analyses en vidéo sur la division immobilière, 
              les marchands de biens et l'investissement immobilier.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Video */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c9a227] uppercase tracking-[0.2em] text-sm font-semibold mb-4">
              Vidéo à la Une
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              {featuredVideo.title}
            </h2>
            <div className="section-separator mx-auto"></div>
          </div>

          <div className="aspect-video w-full rounded-sm overflow-hidden shadow-2xl border-4 border-[#1e3a5f]/10 mb-8">
            <iframe
              src={`https://www.youtube.com/embed/${featuredVideo.id}`}
              title={featuredVideo.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              data-testid="featured-video"
            ></iframe>
          </div>

          <p className="text-gray-600 text-center leading-relaxed max-w-2xl mx-auto">
            {featuredVideo.description}
          </p>
        </div>
      </section>

      {/* YouTube Channel CTA */}
      <section className="py-20 md:py-28 bg-[#f8f9fa]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-8">
            <Youtube className="text-white" size={40} />
          </div>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-6">
            Suivez Notre Chaîne YouTube
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed mb-10">
            Abonnez-vous à notre chaîne pour ne manquer aucune de nos vidéos sur la division immobilière, 
            les marchands de biens et les stratégies d'investissement.
          </p>
          <a 
            href="https://www.youtube.com/@G10visions"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button 
              className="bg-red-600 text-white hover:bg-red-700 rounded-none px-10 py-4 uppercase tracking-wider text-sm font-semibold"
              data-testid="youtube-subscribe-btn"
            >
              <Play className="mr-2" size={18} />
              S'Abonner à la Chaîne
            </Button>
          </a>
        </div>
      </section>

      {/* Topics */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c9a227] uppercase tracking-[0.2em] text-sm font-semibold mb-4">
              Thématiques
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Nos Sujets de Prédilection
            </h2>
            <div className="section-separator mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#f8f9fa] p-8 text-center card-hover">
              <div className="w-16 h-16 bg-[#1e3a5f] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-[#c9a227] text-2xl font-bold">1</span>
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1e3a5f] mb-4">
                Division Immobilière
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Stratégies et techniques pour diviser vos biens et maximiser leur valeur.
              </p>
            </div>
            <div className="bg-[#f8f9fa] p-8 text-center card-hover">
              <div className="w-16 h-16 bg-[#1e3a5f] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-[#c9a227] text-2xl font-bold">2</span>
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1e3a5f] mb-4">
                Marchand de Biens
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Conseils pour réussir vos opérations d'achat-revente immobilière.
              </p>
            </div>
            <div className="bg-[#f8f9fa] p-8 text-center card-hover">
              <div className="w-16 h-16 bg-[#1e3a5f] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-[#c9a227] text-2xl font-bold">3</span>
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1e3a5f] mb-4">
                Investissement
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Analyses et opportunités dans le marché immobilier français.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Videos;
